/**
 * Admin 管理页面
 * 从 MemoryService 迁移，适配 Axiom 架构
 */

import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuthStore } from '@/shared/store/authStore'
import { memoryAdminApi } from '../services/memory-admin-api'
import { ApiClient, apiClients } from '@/shared/services/api'
import type { User } from '@/shared/types/user'
import type { Entity, Relationship } from '../types/memory'
import { 
  ArrowLeft, 
  Users, 
  Brain, 
  Search, 
  Check, 
  X,
  Loader2,
  ChevronDown,
  ChevronUp,
  RefreshCw
} from 'lucide-react'

interface Project {
  project_id: string
  name: string
}

export default function Admin() {
  const navigate = useNavigate()
  const { user, checkAuth } = useAuthStore()
  
  const [activeTab, setActiveTab] = useState<'users' | 'memories'>('users')
  const [users, setUsers] = useState<User[]>([])
  const [isLoadingUsers, setIsLoadingUsers] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  
  // 记忆查看
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)
  const [entities, setEntities] = useState<Entity[]>([])
  const [relationships, setRelationships] = useState<Relationship[]>([])
  const [isLoadingMemories, setIsLoadingMemories] = useState(false)
  const [expandedUser, setExpandedUser] = useState<string | null>(null)
  
  // 项目
  const [_projects, setProjects] = useState<Project[]>([])
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null)
  const [_isLoadingProjects, setIsLoadingProjects] = useState(false)

  // 验证权限
  const [isVerified, setIsVerified] = useState(false)
  
  useEffect(() => {
    let mounted = true
    const verify = async () => {
      const valid = await checkAuth()
      if (!mounted) return
      
      if (!valid) {
        navigate('/memory')
        return
      }
      setIsVerified(true)
    }
    verify()
    
    return () => { mounted = false }
  }, [checkAuth, navigate])

  // 加载项目列表
  const loadProjects = async () => {
    setIsLoadingProjects(true)
    try {
      const response = await ApiClient.getWithClient<{ projects: Project[] }>(
        apiClients.agentAdmin,
        '/projects'
      )
      setProjects(response.data.projects || [])
      // 默认选择第一个项目
      if (response.data.projects?.length > 0 && !selectedProjectId) {
        setSelectedProjectId(response.data.projects[0].project_id)
      }
    } catch (error) {
      console.error('Failed to load projects:', error)
      setProjects([])
    } finally {
      setIsLoadingProjects(false)
    }
  }

  // 加载用户列表
  const loadUsers = async () => {
    setIsLoadingUsers(true)
    try {
      // TODO: 使用 IAM API 搜索用户
      // const response = await ApiClient.getWithClient<{ users: User[]; total: number }>(
      //   apiClients.iam,
      //   '/users/search',
      //   { params: { keyword: '0086', limit: 100 } }
      // )
      // setUsers(response.data.users || [])
      setUsers([])
    } catch (error) {
      console.error('Failed to load users:', error)
      setUsers([])
    } finally {
      setIsLoadingUsers(false)
    }
  }

  // 验证通过后加载用户列表和项目列表
  useEffect(() => {
    if (isVerified && (user?.role === 'admin' || user?.role === 'developer')) {
      loadUsers()
      loadProjects()
    } else if (isVerified && user?.role !== 'admin' && user?.role !== 'developer') {
      navigate('/memory')
    }
  }, [isVerified, user, navigate])
  
  // 项目切换时重新加载记忆
  useEffect(() => {
    if (selectedUserId && selectedProjectId) {
      loadUserMemories(selectedUserId)
    }
  }, [selectedProjectId])

  // 加载用户记忆
  const loadUserMemories = async (userId: string) => {
    setSelectedUserId(userId)
    setIsLoadingMemories(true)
    try {
      const [entitiesRes, relationsRes] = await Promise.all([
        memoryAdminApi.getEntities({ user_id: userId, project_id: selectedProjectId || undefined }),
        memoryAdminApi.getRelationships({ user_id: userId, project_id: selectedProjectId || undefined }),
      ])
      setEntities(entitiesRes.entities)
      setRelationships(relationsRes.relationships)
    } catch (error) {
      console.error('Failed to load memories:', error)
    } finally {
      setIsLoadingMemories(false)
    }
  }

  // 审核用户
  const handleApproveUser = async (_userId: string) => {
    try {
      // TODO: 使用 IAM API 审核用户
      // await ApiClient.postWithClient(apiClients.iam, `/users/${encodeURIComponent(userId)}/approve`)
      loadUsers()
    } catch (error) {
      console.error('Failed to approve user:', error)
    }
  }

  const handleRejectUser = async (_userId: string) => {
    if (!confirm('确定要拒绝该用户的注册申请吗？')) return
    try {
      // TODO: 使用 IAM API 拒绝用户
      // await ApiClient.postWithClient(apiClients.iam, `/users/${encodeURIComponent(userId)}/reject`)
      loadUsers()
    } catch (error) {
      console.error('Failed to reject user:', error)
    }
  }

  // 过滤用户
  const filteredUsers = users.filter(u => 
    u.id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const pendingUsers = filteredUsers.filter(u => u.status === 'pending')
  const activeUsers = filteredUsers.filter(u => u.status === 'active')

  // 状态标签
  const statusLabels: Record<string, { text: string; color: string }> = {
    active: { text: '正常', color: '#34C759' },
    pending: { text: '待审核', color: '#FF9500' },
    rejected: { text: '已拒绝', color: '#FF3B30' },
    disabled: { text: '已禁用', color: '#8E8E93' },
  }

  const roleLabels: Record<string, { text: string; color: string }> = {
    admin: { text: '管理员', color: '#FF6B6B' }, // 使用更柔和的红色，符合苹果设计风格
    developer: { text: '开发者', color: '#007AFF' },
    user: { text: '普通用户', color: '#8E8E93' },
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="px-6 py-4 flex items-center justify-between border-b border-slate-800">
        <div className="flex items-center gap-4">
          <Link to="/memory" className="p-2 rounded-xl text-slate-500 hover:text-white hover:bg-white/5 transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-xl font-bold text-white">管理后台</h1>
            <p className="text-sm text-slate-500">用户管理 & 记忆查看</p>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="px-6 py-4 flex gap-2 border-b border-slate-800">
        <button
          onClick={() => setActiveTab('users')}
          className={`px-4 py-2 rounded-xl text-sm font-medium flex items-center gap-2 transition-colors ${
            activeTab === 'users' ? 'text-white bg-slate-800' : 'text-slate-500 hover:text-white'
          }`}
        >
          <Users className="w-4 h-4" />
          用户管理
          {pendingUsers.length > 0 && (
            <span className="px-1.5 py-0.5 rounded text-xs bg-yellow-500 text-white">
              {pendingUsers.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('memories')}
          className={`px-4 py-2 rounded-xl text-sm font-medium flex items-center gap-2 transition-colors ${
            activeTab === 'memories' ? 'text-white bg-slate-800' : 'text-slate-500 hover:text-white'
          }`}
        >
          <Brain className="w-4 h-4" />
          记忆查看
        </button>
      </div>

      {/* Content */}
      <div className="p-6">
        {activeTab === 'users' && (
          <div className="space-y-6">
            {/* Search */}
            <div className="flex gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="搜索用户..."
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl text-white placeholder-slate-600 bg-slate-900 border border-slate-800"
                />
              </div>
              <button
                onClick={loadUsers}
                className="px-4 py-2.5 rounded-xl text-slate-500 hover:text-white transition-colors flex items-center gap-2 bg-slate-900 border border-slate-800"
              >
                <RefreshCw className="w-4 h-4" />
                刷新
              </button>
            </div>

            {/* Pending Users */}
            {pendingUsers.length > 0 && (
              <div>
                <h2 className="text-sm font-medium text-yellow-400 mb-3">待审核 ({pendingUsers.length})</h2>
                <div className="space-y-2">
                  {pendingUsers.map(u => (
                    <div key={u.id} className="p-4 rounded-xl flex items-center justify-between bg-slate-900 border border-slate-800">
                      <div>
                        <p className="text-white font-medium">{u.name || u.id}</p>
                        <p className="text-sm text-slate-500">{u.email || u.id}</p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleApproveUser(u.id)}
                          className="p-2 rounded-lg transition-colors bg-green-500/10 hover:bg-green-500/20"
                        >
                          <Check className="w-5 h-5 text-green-500" />
                        </button>
                        <button
                          onClick={() => handleRejectUser(u.id)}
                          className="p-2 rounded-lg transition-colors bg-red-500/10 hover:bg-red-500/20"
                        >
                          <X className="w-5 h-5 text-red-500" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Active Users */}
            <div>
              <h2 className="text-sm font-medium text-slate-500 mb-3">所有用户 ({activeUsers.length})</h2>
              {isLoadingUsers ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 text-slate-500 animate-spin" />
                </div>
              ) : (
                <div className="space-y-2">
                  {activeUsers.map(u => {
                    const status = statusLabels[u.status || 'active'] || statusLabels.active
                    const role = roleLabels[u.role || 'user'] || roleLabels.user
                    return (
                      <div key={u.id} className="p-4 rounded-xl bg-slate-900 border border-slate-800">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-white font-medium">{u.name || u.id}</p>
                            <p className="text-sm text-slate-500">{u.email || '-'}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-0.5 rounded text-xs"
                                  style={{ background: `${role.color}20`, color: role.color }}>
                              {role.text}
                            </span>
                            <span className="px-2 py-0.5 rounded text-xs"
                                  style={{ background: `${status.color}20`, color: status.color }}>
                              {status.text}
                            </span>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'memories' && (
          <div className="grid grid-cols-3 gap-6">
            {/* User List */}
            <div className="space-y-2">
              <h2 className="text-sm font-medium text-slate-500 mb-3">选择用户</h2>
              {users.filter(u => u.status === 'active').map(u => (
                <button
                  key={u.id}
                  onClick={() => {
                    setExpandedUser(expandedUser === u.id ? null : u.id)
                    loadUserMemories(u.id)
                  }}
                  className={`w-full p-3 rounded-xl text-left flex items-center justify-between transition-colors ${
                    selectedUserId === u.id 
                      ? 'bg-blue-500/20 border-blue-500/50' 
                      : 'bg-slate-900 border-slate-800'
                  } border`}
                >
                  <span className="text-white">{u.name || u.id}</span>
                  {expandedUser === u.id ? (
                    <ChevronUp className="w-4 h-4 text-slate-500" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-slate-500" />
                  )}
                </button>
              ))}
            </div>

            {/* Entities */}
            <div>
              <h2 className="text-sm font-medium text-slate-500 mb-3">
                实体 {selectedUserId && `(${entities.length})`}
              </h2>
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {isLoadingMemories ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 text-slate-500 animate-spin" />
                  </div>
                ) : !selectedUserId ? (
                  <p className="text-center text-slate-600 py-8">请选择用户</p>
                ) : entities.length === 0 ? (
                  <p className="text-center text-slate-600 py-8">暂无数据</p>
                ) : (
                  entities.map(e => (
                    <div key={e.id} className="p-3 rounded-xl bg-slate-900 border border-slate-800">
                      <div className="flex items-center justify-between">
                        <span className="text-white">{e.name}</span>
                        <span className="text-xs text-slate-500">{e.type}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Relationships */}
            <div>
              <h2 className="text-sm font-medium text-slate-500 mb-3">
                关系 {selectedUserId && `(${relationships.length})`}
              </h2>
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {isLoadingMemories ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 text-slate-500 animate-spin" />
                  </div>
                ) : !selectedUserId ? (
                  <p className="text-center text-slate-600 py-8">请选择用户</p>
                ) : relationships.length === 0 ? (
                  <p className="text-center text-slate-600 py-8">暂无数据</p>
                ) : (
                  relationships.map((r, idx) => (
                    <div key={r.id || idx} className="p-3 rounded-xl bg-slate-900 border border-slate-800">
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-white">{r.source_name || '?'}</span>
                        <span className="text-blue-500">→</span>
                        <span className="text-slate-500">{r.relation_name || r.type || '相关'}</span>
                        <span className="text-blue-500">→</span>
                        <span className="text-white">{r.target_name || '?'}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

